import { mutate as swrMutate } from "swr";
import type { ScopedMutator } from "swr";

/**
 * Model keys whose cached value an invalidation has made untrustworthy.
 *
 * SWR's own `mutate(key)` only re-runs the revalidators of hooks that are mounted
 * at that moment; for a key nobody is subscribed to it leaves the cached value in
 * place (`startRevalidate` in swr's internals returns it as-is). Screens read
 * through `useModel` cope with that by themselves, because plain `useSWR`
 * revalidates on mount when it holds stale data — but `useMultiModel` turns that
 * off, so it needs to be told which cached values are no longer good.
 *
 * MODULE-level for the same reason `inflight` in `hooks.ts` is: a component that
 * suspends on its first render never commits, so its refs and state are reset on
 * every Suspense retry. Only module state survives to tell the retry that the value
 * it can now see in the cache is the stale one.
 */
const staleKeys = new Set<string>();

export const isStale = (key: string) => staleKeys.has(key);

/** Called once a key has been refetched, or once we've given up refetching it. */
export const clearStale = (key: string) => {
  staleKeys.delete(key);
};

/**
 * SWR's `mutate`, with invalidation that also reaches unmounted readers.
 *
 * Import this instead of swr's — calling it with a key alone (the "this model is
 * out of date, go and refetch it" form) marks the key stale on top of what SWR
 * does, so that `useModel` and `useMultiModel` both pick the change up whether or
 * not they happened to be on screen at the time. Every other call shape is passed
 * straight through: supplying data writes a value into the cache, which leaves the
 * key fresh rather than stale, and a matcher function names no single key to mark.
 */
export const mutate = ((key: unknown, ...rest: unknown[]) => {
  // Every model key here is a url string (see `url()` in `common.ts`). SWR
  // serializes other key shapes into its cache key differently, so marking one
  // under `String(key)` would name a key that never gets read — those are left to
  // plain `mutate` semantics.
  if (rest.length === 0 && typeof key === "string" && key) staleKeys.add(key);
  return (swrMutate as (...args: unknown[]) => unknown)(key, ...rest);
}) as ScopedMutator;
